/**
 * Per-event sample rates for outbound OTel emission.
 *
 * Origin: rates extracted verbatim from
 * `src/devtool/telemetry/better-stack-sampler.ts:38-52` (T8401, spec 05
 * §L2830-L2832). Centralised here so the translator (single subscriber)
 * has one decision point per signal.
 *
 * Pinned and frozen — adding a new event MUST be an explicit rate decision,
 * not a default-1.0 fallthrough. Unknown events get rate 0 (never forwarded).
 */

import type { OtlpKeyValue } from './otel-types.js';

/**
 * Rate table keyed by OTel metric/log name. For `morfi.cache.miss`, the
 * per-cause sub-table is consulted via `attrs['morfi.cache.cause']`.
 */
export const SDK_SAMPLE_RATES: Readonly<Record<string, number>> = Object.freeze({
  // Whole-event rates. The translator looks up the event name and applies
  // the listed rate; unknown names default to 0.
  'morfi.error': 1.0, // always — schema/auth/server/validation
  'morfi.error.network': 0.1,
  'morfi.error.realtime-degraded': 0.01,
  'morfi.applyevent_error': 1.0,
  'morfi.onsignal_error': 1.0,
  'morfi.recovery': 1.0,
  'morfi.connectivity.transition': 1.0,
  'morfi.connectivity.transitions': 1.0,
  'morfi.connectivity.mint_recovered': 1.0,
  'morfi.connectivity.degraded_dwell_ms': 1.0,
  'morfi.observer.quarantined': 1.0,
  'morfi.auth.mint': 1.0,
  'morfi.blockmeta': 1.0,
  'morfi.churn.thrash': 1.0,
  'morfi.viewport.unbound': 0.01, // prod 1% sample (dev sees every one)
  'morfi.mutation': 1.0,
  'morfi.cache.miss': 1.0, // gated further by CACHE_MISS_CAUSE_RATES below
  'morfi.realtime.handoff_recovery': 1.0,
  'morfi.position.reconcile_failed': 1.0,
  'morfi.profile_portfolio.realtime_failure': 1.0,
  // Success-path lifecycle (scheduled/completed/coalesced/ready) fires on every trade.
  'morfi.profile_portfolio.realtime': 0.01,
  'morfi.nats.subscriptions.active': 1.0,
  'morfi.nats.reacquire.gap_ms': 1.0,
  'morfi.nats.subscription.gap_ms': 1.0,
  'morfi.nats.ws.concurrent': 1.0,
});

/**
 * Per-code overrides for `morfi.error`, consulted before the category
 * refinement. NATS connect-path failures are `realtime-degraded` (1 %) by
 * category, but a session that cannot reach realtime must be visible on the
 * first failure; `reportNatsFailure` already thins repeated attempts to
 * powers of two, so these are pinned at 100 %.
 */
export const SDK_ERROR_CODE_RATES: Readonly<Record<string, number>> = Object.freeze({
  NATS_CONNECT_FAILED: 1.0,
  NATS_MINT_FAILED: 1.0,
  NATS_WIRE_BOOTSTRAP_FAILED: 1.0,
  NATS_FILTER_UPDATE_FAILED: 1.0,
  NATS_CONNECTION_CLOSED: 1.0,
  NATS_JWT_REFRESH_FAILED: 1.0,
  NATS_CONNECTION_LEAK: 1.0,
});

/**
 * Per-cause sub-table for `morfi.cache.miss`. Mirrors spec L2830-L2832.
 *
 *   schema/auth/server/cold/stale/invalidated-* → 100%
 *   network                                     → 10%
 *   realtime-degraded / schema-invalid          → 1%
 */
export const CACHE_MISS_CAUSE_RATES: Readonly<Record<string, number>> = Object.freeze({
  'schema-invalid': 0.01,
  cold: 1.0,
  auth: 1.0,
  'server-error': 1.0,
  network: 0.1,
  'realtime-degraded': 0.01,
  stale: 1.0,
  'invalidated-by-NATS': 1.0,
  'invalidated-by-tag': 1.0,
  manual: 1.0,
  'SSR-mismatch': 1.0,
});

/**
 * Decide whether this event survives sampling. Returns true iff a uniform
 * [0,1) draw is BELOW the configured rate. Tests can inject a deterministic
 * `random` to drive specific buckets.
 */
export function shouldSample(
  eventName: string,
  attrs: ReadonlyArray<OtlpKeyValue>,
  random: () => number = Math.random,
): boolean {
  let rate = SDK_SAMPLE_RATES[eventName] ?? 0;

  // Cache-miss cause refinement.
  if (eventName === 'morfi.cache.miss') {
    const cause = findStringAttr(attrs, 'morfi.cache.cause');
    if (cause !== undefined) rate = CACHE_MISS_CAUSE_RATES[cause] ?? 0;
  }

  // SdkError refinement: a pinned code wins over its category.
  if (eventName === 'morfi.error') {
    const code = findStringAttr(attrs, 'morfi.error.code');
    const codeRate = code !== undefined ? SDK_ERROR_CODE_RATES[code] : undefined;
    const category = findStringAttr(attrs, 'morfi.error.category');
    if (codeRate !== undefined) rate = codeRate;
    else if (category === 'network') rate = SDK_SAMPLE_RATES['morfi.error.network'] ?? 0.1;
    else if (category === 'realtime-degraded') {
      rate = SDK_SAMPLE_RATES['morfi.error.realtime-degraded'] ?? 0.01;
    }
  }

  if (rate <= 0) return false;
  if (rate >= 1) return true;
  return random() < rate;
}

function findStringAttr(attrs: ReadonlyArray<OtlpKeyValue>, key: string): string | undefined {
  for (const a of attrs) {
    if (a.key === key && a.value.stringValue !== undefined) return a.value.stringValue;
  }
  return undefined;
}
